export interface AvailabilitySchedule {
  days: number[]
  start_time: string
  end_time: string
  // Optional destination for this window, replacing the rule's number while active
  number?: string
}

export interface TransferOverride {
  label?: string
  start_date: string // YYYY-MM-DD, inclusive
  end_date: string // YYYY-MM-DD, inclusive
  mode: 'closed' | 'custom'
  // Repeat every year: the year in the dates is ignored and the month-day
  // range is matched instead, wrapping across New Year (e.g. Dec 24 - Jan 2)
  recurring?: boolean
  availability?: AvailabilitySchedule[]
  closed_instruction?: string
}

const MS_PER_DAY = 86_400_000

/** Today as YYYY-MM-DD in the browser's local timezone */
export function todayISO(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const OVERRIDE_MODES: ReadonlyArray<TransferOverride['mode']> = ['closed', 'custom']

// Strict YYYY-MM-DD check: Date.parse rolls "2026-02-30" over to March 2 in V8,
// so verify the components survive a Date round-trip unchanged
function isRealISODate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return false
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const date = new Date(Date.UTC(year, month - 1, day))
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  )
}

/**
 * Mirrors the backend's malformed-entry skipping: unknown modes and dates that
 * are not real calendar dates (e.g. "2026-02-30") never count as a valid
 * period. For recurring overrides end-before-start is a wrapped month-day
 * range and therefore valid.
 */
export function isValidOverridePeriod(override: TransferOverride): boolean {
  return Boolean(
    override.start_date &&
      override.end_date &&
      (override.recurring || override.start_date <= override.end_date) &&
      OVERRIDE_MODES.includes(override.mode) &&
      isRealISODate(override.start_date) &&
      isRealISODate(override.end_date)
  )
}

const monthDay = (iso: string) => iso.slice(5) // "MM-DD"

export function isOverrideActive(override: TransferOverride, today: string): boolean {
  if (!isValidOverridePeriod(override)) return false
  if (override.recurring) {
    const start = monthDay(override.start_date)
    const end = monthDay(override.end_date)
    const day = monthDay(today)
    return start <= end ? day >= start && day <= end : day >= start || day <= end
  }
  return override.start_date <= today && today <= override.end_date
}

export function isOverrideExpired(override: TransferOverride, today: string): boolean {
  if (override.recurring) return false
  return isValidOverridePeriod(override) && override.end_date < today
}

function periodLengthDays(override: TransferOverride): number {
  if (override.recurring) {
    // Ordinals in a fixed leap year keep Feb 29 representable, mirroring the backend
    const ordinal = (iso: string) => Date.UTC(2000, Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)))
    const diff = (ordinal(override.end_date) - ordinal(override.start_date)) / MS_PER_DAY
    return diff >= 0 ? diff : 366 + diff
  }
  return (Date.parse(override.end_date) - Date.parse(override.start_date)) / MS_PER_DAY
}

/**
 * Mirrors the backend precedence: the active override with the shortest period
 * wins; equal-length ties go to the earliest start date, so precedence does not
 * depend on list order (identical periods fall back to the first listed, but
 * saving those is blocked).
 */
export function getActiveOverride(
  overrides: TransferOverride[] | undefined,
  today: string
): TransferOverride | null {
  let active: TransferOverride | null = null
  let activeLength = Infinity
  let activeStart = ''
  for (const override of overrides ?? []) {
    if (!isOverrideActive(override, today)) continue
    const length = periodLengthDays(override)
    if (active === null || length < activeLength || (length === activeLength && override.start_date < activeStart)) {
      active = override
      activeLength = length
      activeStart = override.start_date
    }
  }
  return active
}

export function countUpcomingOverrides(
  overrides: TransferOverride[] | undefined,
  today: string
): number {
  return (overrides ?? []).filter((override) => {
    if (!isValidOverridePeriod(override)) return false
    // A recurring override that is not active right now always has a next occurrence
    if (override.recurring) return !isOverrideActive(override, today)
    return override.start_date > today
  }).length
}

/**
 * Legacy closed_dates are shown and saved as one-day closed exception periods.
 * The backend keeps honoring closed_dates for configs that were never re-saved.
 */
export function closedDatesToOverrides(closedDates: string[] | undefined): TransferOverride[] {
  return [...new Set((closedDates ?? []).filter(Boolean))].map((date) => ({
    start_date: date,
    end_date: date,
    mode: 'closed' as const,
  }))
}

/**
 * Merge legacy closed_dates into an override list for editing. Dates already
 * covered by an identical closed override are skipped (true duplicates would
 * block saving). A custom override on the same day does NOT swallow the date:
 * the backend lets closed_dates win, so the closed exception is kept and the
 * duplicate-period validation surfaces the conflict to the user.
 */
export function mergeLegacyClosedDates(
  overrides: TransferOverride[],
  closedDates: string[] | undefined
): TransferOverride[] {
  const existingClosedPeriods = new Set(
    overrides
      .filter((o) => o.mode === 'closed' && !o.recurring && isValidOverridePeriod(o))
      .map((o) => `${o.start_date}|${o.end_date}`)
  )
  const migrated = closedDatesToOverrides(closedDates).filter(
    (o) => !existingClosedPeriods.has(`${o.start_date}|${o.end_date}`)
  )
  return [...overrides, ...migrated]
}

export function sortOverridesByStartDate(overrides: TransferOverride[]): TransferOverride[] {
  return [...overrides].sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''))
}

const ALL_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]

/**
 * The weekdays (1=Monday..7=Sunday) that actually occur within the inclusive
 * period. Used to restrict the day picker for short exception periods, so a
 * one-day exception cannot silently end up with a schedule that never matches
 * its own date. Invalid or week-spanning periods return all weekdays.
 */
export function weekdaysInPeriod(startDate: string, endDate: string): number[] {
  const period = { start_date: startDate, end_date: endDate, mode: 'closed' as const }
  if (!isValidOverridePeriod(period)) return ALL_WEEKDAYS
  const startMs = Date.parse(startDate)
  const endMs = Date.parse(endDate)
  if ((endMs - startMs) / MS_PER_DAY >= 6) return ALL_WEEKDAYS
  const days = new Set<number>()
  for (let ms = startMs; ms <= endMs; ms += MS_PER_DAY) {
    const jsDay = new Date(ms).getUTCDay() // 0=Sunday..6=Saturday
    days.add(jsDay === 0 ? 7 : jsDay)
  }
  return [...days].sort((a, b) => a - b)
}

export function hasOverlappingOverrides(overrides: TransferOverride[]): boolean {
  // Recurring periods are excluded: full-date interval math does not apply to
  // them, and overlap with a recurring period is usually intentional nesting
  const valid = overrides.filter((o) => isValidOverridePeriod(o) && !o.recurring)
  for (let i = 0; i < valid.length; i++) {
    for (let j = i + 1; j < valid.length; j++) {
      if (valid[i].start_date <= valid[j].end_date && valid[j].start_date <= valid[i].end_date) {
        return true
      }
    }
  }
  return false
}

/**
 * Indexes of non-expired overrides whose exact period (start and end date)
 * duplicates an earlier one. Two overrides with an identical period have no
 * meaningful order, so this should block saving.
 */
export function findDuplicatePeriodIndexes(overrides: TransferOverride[], today: string): number[] {
  const seen = new Set<string>()
  const duplicates: number[] = []
  overrides.forEach((override, idx) => {
    if (!isValidOverridePeriod(override) || isOverrideExpired(override, today)) return
    // Recurring periods are duplicates on their month-day range, whatever the years say
    const key = override.recurring
      ? `recurring|${monthDay(override.start_date)}|${monthDay(override.end_date)}`
      : `${override.start_date}|${override.end_date}`
    if (seen.has(key)) {
      duplicates.push(idx)
    } else {
      seen.add(key)
    }
  })
  return duplicates
}

/**
 * Whether the override at `index` wins on at least one day from today onward.
 * An override that is always beaten by shorter overlapping overrides never
 * takes effect and deserves a warning. Expired and invalid overrides are
 * treated as effective (they are flagged through other means), as are periods
 * too long to simulate.
 */
export function overrideHasFutureEffect(
  overrides: TransferOverride[],
  index: number,
  today: string,
  maxDays = 750
): boolean {
  const override = overrides[index]
  if (!isValidOverridePeriod(override) || isOverrideExpired(override, today)) return true
  // Recurring overrides recur indefinitely; day-by-day simulation does not apply
  if (override.recurring) return true
  const startMs = Math.max(Date.parse(override.start_date), Date.parse(today))
  const endMs = Date.parse(override.end_date)
  if ((endMs - startMs) / MS_PER_DAY > maxDays) return true
  for (let ms = startMs; ms <= endMs; ms += MS_PER_DAY) {
    const date = new Date(ms).toISOString().split('T')[0]
    if (getActiveOverride(overrides, date) === override) return true
  }
  return false
}
